import { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

const EmpresaContext = createContext();

export const useEmpresa = () => {
  const context = useContext(EmpresaContext);
  if (!context) {
    throw new Error('useEmpresa debe ser usado dentro de EmpresaProvider');
  }
  return context;
};

export const EmpresaProvider = ({ children }) => {
  // Obtener empresas del Redux store
  const companiesFromRedux = useSelector(state => state.auth.companies);

  const [empresaActiva, setEmpresaActiva] = useState(() => {
    // Cargar empresa activa desde localStorage al inicializar
    const savedEmpresa = localStorage.getItem('empresaActiva');
    if (savedEmpresa) {
      const empresa = JSON.parse(savedEmpresa);
      // Mapear campos del backend al frontend si es necesario
      return {
        id: empresa.id,
        nombreFantasia: empresa.tradeName || empresa.nombreFantasia,
        razonSocial: empresa.tradeName || empresa.razonSocial,
        rut: empresa.taxId || empresa.rut,
        logo: empresa.logo,
        plan: empresa.plan,
        rolUsuario: empresa.role || empresa.rolUsuario,
        permisos: empresa.permissions || empresa.permisos,
        permissions: empresa.permissions || empresa.permisos,
        // Mantener campos originales también por compatibilidad
        tradeName: empresa.tradeName,
        taxId: empresa.taxId,
        role: empresa.role
      };
    }
    return null;
  });
  const [empresasUsuario, setEmpresasUsuario] = useState([]);
  const [permisos, setPermisos] = useState(() => {
    // Cargar permisos desde la empresa guardada
    const savedEmpresa = localStorage.getItem('empresaActiva');
    if (savedEmpresa) {
      const empresa = JSON.parse(savedEmpresa);
      return empresa.permisos || {};
    }
    return {};
  });

  // Ref para evitar llamadas duplicadas en StrictMode
  const companiesLoadedRef = useRef(false);
  const lastCompaniesRef = useRef(null);

  // Mock de empresas - después vendrá de la API
  const empresasDisponibles = [
    {
      id: 1,
      rut: '76.123.456-7',
      razonSocial: 'INGENIERÍA NORTE SPA',
      nombreFantasia: 'Ingeniería Norte',
      giro: 'Venta de equipos industriales para minería',
      direccion: 'Av. Providencia 1234, Providencia, Santiago',
      comuna: 'Providencia',
      ciudad: 'Santiago',
      telefono: '+56 2 2345 6789',
      email: 'contacto@ingenierianorte.cl',
      logo: '/logo-empresa1.png',
      monedaDefault: 'CLP',
      margenMinimo: 12,
      diasPagoDefault: 30,
      plan: 'professional',
      estado: 'activa',
      // Rol del usuario actual en esta empresa
      rolUsuario: 'admin',
      permisos: {
        crear_cotizaciones: true,
        aprobar_cotizaciones: true,
        ver_margenes: true,
        ver_costos: true,
        gestionar_usuarios: true,
        configurar_empresa: true
      }
    },
    {
      id: 2,
      rut: '76.234.567-8',
      razonSocial: 'SUPPLY CHAIN MINERO LTDA',
      nombreFantasia: 'Supply Chain Minero',
      giro: 'Importación y distribución de repuestos',
      direccion: 'Av. Las Condes 9876, Las Condes, Santiago',
      comuna: 'Las Condes',
      ciudad: 'Santiago',
      telefono: '+56 2 2876 5432',
      email: 'ventas@supplychainminero.cl',
      logo: '/logo-empresa2.png',
      monedaDefault: 'USD',
      margenMinimo: 15,
      diasPagoDefault: 45,
      plan: 'starter',
      estado: 'activa',
      // Rol del usuario actual en esta empresa
      rolUsuario: 'vendedor',
      permisos: {
        crear_cotizaciones: true,
        aprobar_cotizaciones: false,
        ver_margenes: false,
        ver_costos: false,
        gestionar_usuarios: false,
        configurar_empresa: false
      }
    },
    {
      id: 3,
      rut: '76.345.678-9',
      razonSocial: 'SERVICIOS TÉCNICOS ATACAMA SA',
      nombreFantasia: 'Servicios Atacama',
      giro: 'Mantención y reparación de equipos',
      direccion: 'Calle Copiapó 567, Calama',
      comuna: 'Calama',
      ciudad: 'Calama',
      telefono: '+56 55 234 5678',
      email: 'servicios@atacama.cl',
      logo: '/logo-empresa3.png',
      monedaDefault: 'UF',
      margenMinimo: 18,
      diasPagoDefault: 30,
      plan: 'enterprise',
      estado: 'activa',
      // Rol del usuario actual en esta empresa
      rolUsuario: 'visor',
      permisos: {
        crear_cotizaciones: false,
        aprobar_cotizaciones: false,
        ver_margenes: false,
        ver_costos: false,
        gestionar_usuarios: false,
        configurar_empresa: false
      }
    }
  ];

  // Cargar empresas del usuario desde Redux o localStorage
  useEffect(() => {
    try {
      // Evitar procesamiento duplicado si las empresas no han cambiado
      const companiesStr = JSON.stringify(companiesFromRedux);
      if (lastCompaniesRef.current === companiesStr) {
        return;
      }
      lastCompaniesRef.current = companiesStr;

      console.log('🔍 Verificando empresas - Redux:', companiesFromRedux, 'LocalStorage userCompanies:', localStorage.getItem('userCompanies'));

      // Priorizar empresas de Redux si están disponibles
      let userCompanies = companiesFromRedux;

      // Si Redux no tiene empresas, intentar cargar de localStorage
      if (!userCompanies || userCompanies.length === 0) {
        const userCompaniesStr = localStorage.getItem('userCompanies');
        if (userCompaniesStr) {
          userCompanies = JSON.parse(userCompaniesStr);
          console.log('📊 Empresas cargadas desde localStorage:', userCompanies);
        } else {
          // Si no hay userCompanies pero hay empresaActiva, usar esa como única empresa
          const savedEmpresa = localStorage.getItem('empresaActiva');
          if (savedEmpresa) {
            const empresa = JSON.parse(savedEmpresa);
            console.log('📊 Usando empresaActiva como única empresa:', empresa);
            userCompanies = [empresa];
          }
        }
      } else {
        console.log('📊 Empresas cargadas desde Redux:', userCompanies);
      }

      if (userCompanies && userCompanies.length > 0) {
        // Mapear los campos del backend a los del frontend
        const empresasMapeadas = userCompanies.map(company => ({
          id: company.id,
          nombreFantasia: company.tradeName || company.nombreFantasia,
          razonSocial: company.tradeName || company.razonSocial, // Usar el mismo hasta que tengamos razonSocial del backend
          rut: company.taxId || company.rut,
          logo: company.logo,
          plan: company.plan,
          rolUsuario: company.role || company.rolUsuario,
          permisos: company.permissions || company.permisos,
          permissions: company.permissions || company.permisos // Mantener ambas formas
        }));

        console.log('📊 Empresas mapeadas:', empresasMapeadas);
        setEmpresasUsuario(empresasMapeadas);
        console.log('✅ setEmpresasUsuario llamado con:', empresasMapeadas);

        // Si no hay empresa activa y hay empresas disponibles, seleccionar la primera
        if (!empresaActiva && empresasMapeadas.length > 0) {
          console.log('✅ Seleccionando primera empresa:', empresasMapeadas[0]);
          setEmpresaActiva(empresasMapeadas[0]);
          localStorage.setItem('empresaActiva', JSON.stringify(empresasMapeadas[0]));
          setPermisos(empresasMapeadas[0].permissions || empresasMapeadas[0].permisos || {});
        }
      } else {
        console.log('⚠️ No se encontraron empresas en ningún lado');
      }
    } catch (error) {
      console.error('❌ Error cargando empresas:', error);
    }
  }, [companiesFromRedux]); // Solo re-ejecutar cuando cambien las empresas de Redux

  const cambiarEmpresa = (empresa) => {
    console.log('🔄 Cambiando a empresa:', empresa);

    if (empresa) {
      // Mapear campos del backend al frontend
      const empresaMapeada = {
        id: empresa.id,
        nombreFantasia: empresa.tradeName || empresa.nombreFantasia,
        razonSocial: empresa.tradeName || empresa.razonSocial,
        rut: empresa.taxId || empresa.rut,
        logo: empresa.logo,
        plan: empresa.plan,
        rolUsuario: empresa.role || empresa.rolUsuario,
        permisos: empresa.permissions || empresa.permisos,
        permissions: empresa.permissions || empresa.permisos,
        // Mantener campos originales también por compatibilidad
        tradeName: empresa.tradeName,
        taxId: empresa.taxId,
        role: empresa.role
      };

      setEmpresaActiva(empresaMapeada);

      // Mapear permisos
      setPermisos(empresaMapeada.permissions || empresaMapeada.permisos || {});

      // Guardar en localStorage
      localStorage.setItem('empresaActiva', JSON.stringify(empresaMapeada));
    } else {
      setEmpresaActiva(null);
      setPermisos({});
      localStorage.removeItem('empresaActiva');
    }
  };

  const obtenerClientesPorEmpresa = (empresaId) => {
    // Mock de clientes por empresa
    const clientesPorEmpresa = {
      1: [ // INGENIERÍA NORTE SPA
        {
          id: 1,
          nombre: 'BHP Billiton',
          rut: '76.123.456-7',
          categoria: 'A',
          credito: 500000000,
          deudaActual: 123000000,
          email: 'compras@bhp.com',
          telefono: '+56 9 8765 4321',
          monedaPreferida: 'USD'
        },
        {
          id: 2,
          nombre: 'Codelco División Andina',
          rut: '61.704.000-K',
          categoria: 'A',
          credito: 600000000,
          deudaActual: 200000000,
          email: 'abastecimiento@codelco.cl',
          telefono: '+56 9 8765 6666',
          monedaPreferida: 'UF'
        }
      ],
      2: [ // SUPPLY CHAIN MINERO
        {
          id: 3,
          nombre: 'Minera Los Pelambres',
          rut: '96.790.240-3',
          categoria: 'B',
          credito: 300000000,
          deudaActual: 50000000,
          email: 'compras@pelambres.cl',
          telefono: '+56 53 321 4567',
          monedaPreferida: 'USD'
        }
      ],
      3: [ // SERVICIOS ATACAMA
        {
          id: 4,
          nombre: 'Minera Escondida',
          rut: '76.234.567-8',
          categoria: 'A',
          credito: 450000000,
          deudaActual: 89000000,
          email: 'servicios@escondida.cl',
          telefono: '+56 55 444 5555',
          monedaPreferida: 'CLP'
        }
      ]
    };

    return clientesPorEmpresa[empresaId] || [];
  };

  const obtenerProveedoresPorEmpresa = (empresaId) => {
    // Mock de proveedores por empresa
    const proveedoresPorEmpresa = {
      1: [
        { id: 1, nombre: 'WEIR Chile', especialidad: 'Válvulas y bombas', atiende24x7: true },
        { id: 2, nombre: 'Komatsu Chile', especialidad: 'Maquinaria pesada', atiende24x7: true },
        { id: 3, nombre: 'SKF Chile', especialidad: 'Rodamientos', atiende24x7: true }
      ],
      2: [
        { id: 4, nombre: 'Importadora China SA', especialidad: 'Repuestos genéricos', atiende24x7: false },
        { id: 5, nombre: 'USA Parts Corp', especialidad: 'Repuestos OEM', atiende24x7: true }
      ],
      3: [
        { id: 6, nombre: 'Taller Mecánico Norte', especialidad: 'Reparaciones', atiende24x7: true },
        { id: 7, nombre: 'Soldaduras Especiales', especialidad: 'Soldaduras', atiende24x7: false }
      ]
    };

    return proveedoresPorEmpresa[empresaId] || [];
  };

  const puedeAcceder = (accion) => {
    return permisos[accion] || false;
  };

  const value = useMemo(() => {
    console.log('🔄 Context value actualizado - empresasUsuario:', empresasUsuario.length);
    return {
      empresaActiva,
      empresasUsuario,
      permisos,
      cambiarEmpresa,
      obtenerClientesPorEmpresa,
      obtenerProveedoresPorEmpresa,
      puedeAcceder
    };
  }, [empresaActiva, empresasUsuario, permisos]);

  return (
    <EmpresaContext.Provider value={value}>
      {children}
    </EmpresaContext.Provider>
  );
};

export default EmpresaContext;